import { useGeolocation } from "@uidotdev/usehooks";
import ms from "ms";
import { useMemo } from "react";

import { animatedIconMap } from "@/constants/animatedIcon";
import { useGetIpLocationQuery, useGetWeatherByCoordsQuery } from "@/services/weatherApi";

const THIRTY_MINUTES = ms("30m");
const FALLBACK_ICON = animatedIconMap["03d"];

/** ISO-3166 alpha-2 → display name, using the platform's own table (no extra request, no dependency). */
const countryName = (code: string | undefined): string | null => {
  if (!code) return null;
  try {
    return new Intl.DisplayNames(undefined, { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
};

export type WeatherStatus = "loading" | "ready" | "unavailable";

export interface IWeatherView {
  status: WeatherStatus;
  iconSrc: string;
  description: string | null;
  condition: string | null;
  place: string | null;
  temp: number | null;
  feelsLike: number | null;
  tempMin: number | null;
  tempMax: number | null;
  humidity: number | null;
  /** Converted from the API's m/s to km/h, which reads better at a glance. */
  windKph: number | null;
  refetch: () => void;
}

/**
 * Resolves the viewer's coordinates (GPS, falling back to IP lookup when the
 * permission is denied or unavailable) and turns the weather payload into a
 * flat view model.
 *
 * The city/country come from the weather response itself — it already carries
 * `name` and `sys.country`, so the previous reverse-geocode round-trip to
 * Nominatim was fetching data we were being handed anyway (and browser-side use
 * of that public endpoint is against its usage policy). `Intl.DisplayNames`
 * expands the ISO code locally, so full country names survive the removal.
 */
export const useWeather = (): IWeatherView => {
  const {
    latitude,
    longitude,
    loading: geoLoading,
    error: geoError,
  } = useGeolocation({ enableHighAccuracy: false, maximumAge: THIRTY_MINUTES });

  const hasGpsCoords = latitude != null && longitude != null;
  const useIpFallback = !geoLoading && (!!geoError || !hasGpsCoords);

  const {
    data: ipLoc,
    isLoading: isFetchingIp,
    isError: ipFailed,
  } = useGetIpLocationQuery(undefined, {
    skip: !useIpFallback,
    refetchOnFocus: false,
    refetchOnMountOrArgChange: false,
  });

  const ipLat = ipLoc?.latitude != null ? parseFloat(ipLoc.latitude) : null;
  const ipLon = ipLoc?.longitude != null ? parseFloat(ipLoc.longitude) : null;
  const lat = hasGpsCoords ? latitude! : ipLat;
  const lon = hasGpsCoords ? longitude! : ipLon;
  const hasCoords = lat != null && lon != null && !Number.isNaN(lat) && !Number.isNaN(lon);

  const {
    data: weather,
    isLoading: isFetchingWeather,
    isError: weatherFailed,
    refetch,
  } = useGetWeatherByCoordsQuery(
    { lat: lat!, lon: lon! },
    { skip: !hasCoords, refetchOnFocus: false, refetchOnMountOrArgChange: false },
  );

  return useMemo<IWeatherView>(() => {
    const resolvingCoords = geoLoading || (useIpFallback && isFetchingIp);
    const coordsUnavailable = !resolvingCoords && !hasCoords;

    const status: WeatherStatus =
      coordsUnavailable || weatherFailed || ipFailed
        ? "unavailable"
        : resolvingCoords || isFetchingWeather || !weather
          ? "loading" : "ready";

    const city = weather?.name ?? ipLoc?.city ?? null;
    const country = countryName(weather?.sys?.country) ?? ipLoc?.country ?? null;

    return {
      status,
      iconSrc: weather?.weather[0]?.icon
        ? (animatedIconMap[weather.weather[0].icon] ?? FALLBACK_ICON)
        : FALLBACK_ICON,
      description: weather?.weather[0]?.description ?? null,
      condition: weather?.weather[0]?.main ?? null,
      place: [city, country].filter(Boolean).join(", ") || null,
      temp: weather ? Math.round(weather.main.temp) : null,
      feelsLike: weather ? Math.round(weather.main.feels_like) : null,
      tempMin: weather ? Math.round(weather.main.temp_min) : null,
      tempMax: weather ? Math.round(weather.main.temp_max) : null,
      humidity: weather?.main.humidity ?? null,
      windKph: weather ? Math.round(weather.wind.speed * 3.6) : null,
      refetch,
    };
  }, [
    geoLoading,
    useIpFallback,
    isFetchingIp,
    hasCoords,
    isFetchingWeather,
    weather,
    weatherFailed,
    ipFailed,
    ipLoc,
    refetch,
  ]);
};
